import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Check, Loader2, Lock, PenLine, Play, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import PageBlobs from "@/components/PageBlobs";
import GlowSpecks from "@/components/GlowSpecks";
import { apiFetch, getToken } from "@/api";

const loadRazorpay = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = () =>
      reject(new Error("Unable to load the payment gateway. Please try again."));
    document.body.appendChild(s);
  });

/* Card copy only — every price/limit is enforced server-side by the backend. */
const FREE_CARD = {
  name: "Free",
  billing: "monthly",
  price: "₹0",
  period: "",
  subtext: null,
  credits: "Ask Abhyas not included",
  creditsLocked: true,
  badge: null,
  features: [
    { text: "AI Practice — 1 generation (one-time, lifetime)", locked: false },
    { text: "Watch & Learn — Not included", locked: true },
    { text: "Ask Abhyas (AI chat) — Not included", locked: true },
    { text: "Basic progress & streak tracking", locked: false },
    { text: "Practice history — last 3 sessions", locked: false },
    { text: "CBSE & ICSE · Classes 9–12", locked: false },
    { text: "Full practice history", locked: true },
    { text: "Detailed performance insights", locked: true },
  ],
  emphasis: "plain",
  free: true,
  cta: "Start Free",
};

/* Shared feature lists — every card states all three limits explicitly */
const STARTER_FEATURES = [
  { text: "Everything included in Free", locked: false },
  { text: "AI Practice — 4 generations/day", locked: false },
  { text: "Watch & Learn — 1 video/day", locked: false },
  { text: "Ask Abhyas — 20 credits/month", locked: false },
  { text: "Full practice history", locked: false },
  { text: "Daily statistics & 7-day chart", locked: false },
  { text: "Limited Abhyas AI chat history", locked: false },
];
const PRO_FEATURES = [
  { text: "Everything in Starter", locked: false },
  { text: "AI Practice — 8 generations/day", locked: false },
  { text: "Watch & Learn — 2 videos/day", locked: false },
  { text: "Ask Abhyas — 50 credits/month", locked: false },
  { text: "Subject & chapter progress", locked: false },
  { text: "Personalized practice based on performance", locked: false },
  { text: "Long-term progress & streak insights", locked: false },
  { text: "Downloadable PDFs", locked: false },
  { text: "Abhyas AI chat history — up to 30 days", locked: false },
];
const ELITE_FEATURES = [
  { text: "Everything in Pro", locked: false },
  { text: "AI Practice — 15 generations/day", locked: false },
  { text: "Watch & Learn — 5 videos/day", locked: false },
  { text: "Ask Abhyas — 100 credits/month", locked: false },
  { text: "Abhyas AI chat history — up to 30 days", locked: false },
];

/* 7-Day Pass — one-time payment, no auto-renewal, no subscription */
const WEEKLY_PASS = {
  name: "Weekly",
  billing: "weekly",
  label: "7-Day Pass",
  price: "₹59",
  period: "/ 7 days",
  features: [
    { icon: "practice", text: "AI Practice — 2 generations/day" },
    { icon: "video", text: "Watch & Learn — 1 video/day" },
    { icon: "chat", text: "Ask Abhyas — 5 credits for the week" },
  ],
  note: "One-time payment · No auto-renewal · No subscription",
  cta: "Get the 7-Day Pass",
};

const MONTHLY_CARDS = [
  FREE_CARD,
  {
    name: "Starter",
    billing: "monthly",
    price: "₹249",
    period: "/month",
    subtext: null,
    credits: "20 Prompt Learning credits/month",
    badge: null,
    features: STARTER_FEATURES,
    emphasis: "plain",
    cta: "Choose Starter",
  },
  {
    name: "Pro",
    billing: "monthly",
    price: "₹499",
    period: "/month",
    subtext: null,
    credits: "50 Prompt Learning credits/month",
    badge: "Most Popular",
    features: PRO_FEATURES,
    emphasis: "top",
    cta: "Choose Pro",
  },
  {
    name: "Elite",
    billing: "monthly",
    price: "₹999",
    period: "/month",
    subtext: null,
    credits: "100 Prompt Learning credits/month",
    badge: null,
    features: ELITE_FEATURES,
    emphasis: "plain",
    cta: "Choose Elite",
  },
];

const QUARTERLY_CARDS = [
  FREE_CARD,
  {
    name: "Starter",
    billing: "quarterly",
    price: "₹599",
    period: "/3 months",
    subtext: "≈ ₹200/month · Save ₹148",
    credits: "20 Prompt Learning credits/month",
    creditsNote: "Credits added monthly — 20 every month",
    badge: null,
    features: STARTER_FEATURES,
    emphasis: "plain",
    cta: "Choose Starter",
  },
  {
    name: "Pro",
    billing: "quarterly",
    price: "₹1,249",
    period: "/3 months",
    subtext: "≈ ₹416/month · Save ₹248",
    credits: "50 Prompt Learning credits/month",
    creditsNote: "Credits added monthly — 50 every month",
    badge: "Most Popular",
    features: PRO_FEATURES,
    emphasis: "top",
    cta: "Choose Pro",
  },
  {
    name: "Elite",
    billing: "quarterly",
    price: "₹2,499",
    period: "/3 months",
    subtext: "≈ ₹833/month · Save ₹498",
    credits: "100 Prompt Learning credits/month",
    creditsNote: "Credits added monthly — 100 every month",
    badge: null,
    features: ELITE_FEATURES,
    emphasis: "plain",
    cta: "Choose Elite",
  },
];

const CREDIT_POINTS = [
  "Prompt Learning credits are used when you ask Abhyas AI questions.",
  "Simple questions use 1 credit; more demanding requests may use 2.",
  "Failed AI requests never consume credits.",
  "Unused credits carry forward to the next month, up to your plan's limit.",
  "On quarterly plans, credits are added every month — not all at once.",
  "The 7-Day Pass is a single ₹59 payment — no auto-renewal, no subscription.",
];

/* The plans UI — shared by the Paywall modal and the homepage Pricing section */
export const PricingPlans = ({ onActivated, hideFree = false }) => {
  const navigate = useNavigate();
  const [loadingKey, setLoadingKey] = useState(null);
  const [me, setMe] = useState(null);
  const [qr, setQr] = useState(null);
  const [billing, setBilling] = useState("monthly");

  useEffect(() => {
    if (!getToken()) return;
    apiFetch("/me").then(setMe).catch(() => {});
  }, []);

  const refreshMe = () => {
    if (getToken()) apiFetch("/me").then(setMe).catch(() => {});
  };

  const activatedToast = (p) =>
    p.billing === "weekly"
      ? "7-Day Pass activated — enjoy your week!"
      : `${p.name} plan activated — welcome aboard!`;

  // Poll the QR payment status while the panel is visible
  useEffect(() => {
    if (!qr) return;
    const t = setInterval(async () => {
      try {
        const res = await apiFetch(`/payments/upi-qr/${qr.qr_id}/status`);
        if (res.status === "paid") {
          setQr(null);
          toast.success(activatedToast(qr));
          refreshMe();
          onActivated?.();
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(t);
  }, [qr]);

  const currentPlan =
    me && me.planStatus === "active" && ["Starter", "Pro", "Elite"].includes(me.plan)
      ? me.plan
      : "Free";
  const allCards = billing === "quarterly" ? QUARTERLY_CARDS : MONTHLY_CARDS;
  // Public homepage passes hideFree — Free is never a purchasable option there
  const cards = hideFree ? allCards.filter((p) => !p.free) : allCards;

  const handleUnlock = async (p) => {
    if (!getToken()) {
      toast.error("Please log in to continue.");
      return;
    }
    // Phones: show a scannable UPI QR first (intent apps can fail, e.g. GPay
    // on some browsers); every other payment mode remains one tap away.
    if (/iPhone|iPod|Android.+Mobile/i.test(navigator.userAgent)) {
      openQrPanel(p);
      return;
    }
    openCheckout(p);
  };

  const openQrPanel = async (p) => {
    setLoadingKey(`${p.name}:${p.billing}`);
    try {
      const data = await apiFetch("/payments/upi-qr", {
        method: "POST",
        body: { plan: p.name, billing: p.billing },
      });
      setQr(data);
    } catch {
      toast.error(
        "QR payment is unavailable right now — opening secure checkout instead."
      );
      openCheckout(p);
    } finally {
      setLoadingKey(null);
    }
  };

  const openCheckout = async (p) => {
    setLoadingKey(`${p.name}:${p.billing}`);
    try {
      const order = await apiFetch("/payments/create-order", {
        method: "POST",
        body: { plan: p.name, billing: p.billing },
      });
      await loadRazorpay();
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Abhyas",
        description: p.billing === "weekly" ? "7-Day Pass (one-time, 7 days)" : `${p.name} plan${p.billing === "quarterly" ? " (3 months)" : ""}`,
        order_id: order.order_id,
        theme: { color: "#16A34A" },
        config: {
          display: {
            blocks: {
              upi: {
                name: "Pay via UPI",
                instruments: [
                  {
                    method: "upi",
                    flows: ["intent", "qr"],
                  },
                ],
              },
              other: {
                name: "Other Payment Modes",
                instruments: [{ method: "card" }, { method: "netbanking" }],
              },
            },
            sequence: ["block.upi", "block.other"],
          },
        },
        handler: async (resp) => {
          try {
            await apiFetch("/payments/verify", {
              method: "POST",
              body: {
                plan: p.name,
                billing: p.billing,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              },
            });
            toast.success(activatedToast(p));
            refreshMe();
            onActivated?.();
          } catch (err) {
            toast.error(err.message);
          }
        },
        modal: {
          ondismiss: () =>
            toast.error("Payment cancelled — you haven't been charged."),
        },
      });
      rzp.on("payment.failed", () =>
        toast.error("Payment failed — you haven't been charged. Please try again.")
      );
      rzp.open();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <div>
      {/* Billing mode toggle */}
      <div className="mt-6 flex justify-center">
        <div
          data-testid="billing-toggle"
          className="inline-flex rounded-full border border-[#E3ECE6] bg-white p-1 shadow-sm"
        >
          {[
            { id: "monthly", label: "Monthly" },
            { id: "quarterly", label: "Quarterly" },
          ].map((m) => (
            <button
              key={m.id}
              data-testid={`billing-${m.id}`}
              onClick={() => setBilling(m.id)}
              aria-pressed={billing === m.id}
              className={`min-w-[110px] rounded-full px-6 py-2.5 text-sm font-bold transition-[background-color,color,box-shadow] duration-200 ${
                billing === m.id
                  ? "bg-[#16A34A] text-white shadow-sm"
                  : "text-[#17201A] hover:text-[#16A34A]"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {qr ? (
        <div
          data-testid="upi-qr-panel"
          className="animate-fade-up mx-auto mt-10 max-w-sm rounded-[1.5rem] border border-[#E3ECE6] bg-white p-8 text-center"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B45309]">
            Scan &amp; Pay
          </p>
          <h3 className="mt-2 font-display text-2xl font-extrabold text-[#17201A]">
            {qr.billing === "weekly" ? "7-Day Pass" : qr.plan} · ₹{(qr.amount / 100).toLocaleString("en-IN")}
            {qr.billing === "quarterly" && (
              <span className="block text-sm font-bold text-[#647067]">for 3 months</span>
            )}
            {qr.billing === "weekly" && (
              <span className="block text-sm font-bold text-[#647067]">one-time · for 7 days</span>
            )}
          </h3>
          <img
            data-testid="upi-qr-image"
            src={qr.image_url}
            alt={`UPI QR code for the ${qr.plan} plan`}
            className="mx-auto mt-6 w-60 max-w-full rounded-2xl border border-[#E3ECE6] bg-white p-2"
          />
          <p className="mt-5 text-sm font-semibold leading-relaxed text-[#647067]">
            Scan with any UPI app — Google Pay, PhonePe, Paytm, BHIM. On the
            same phone, take a screenshot and pick it in your UPI app's QR
            scanner.
          </p>
          <p
            data-testid="upi-qr-waiting"
            className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-[#16A34A]"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Waiting for payment — this updates automatically
          </p>
          <div className="mt-6 space-y-2.5">
            <button
              data-testid="upi-qr-other-methods-btn"
              onClick={() => openCheckout({ name: qr.plan, billing: qr.billing || "monthly" })}
              disabled={loadingKey !== null}
              className="w-full rounded-full bg-[#16A34A] py-3.5 text-sm font-bold text-white transition-[background-color,transform] duration-200 hover:bg-[#15803D] disabled:opacity-60"
            >
              {loadingKey ? "Opening checkout..." : "Pay with UPI app / card instead"}
            </button>
            <button
              data-testid="upi-qr-back-btn"
              onClick={() => setQr(null)}
              className="w-full rounded-full border border-[#E3ECE6] py-3 text-sm font-bold text-[#647067] transition-colors duration-200 hover:text-[#17201A]"
            >
              Back to plans
            </button>
          </div>
        </div>
      ) : (
      <>
      {/* 7-Day Pass — full-width one-time banner above all plan cards */}
      <div
        data-testid="weekly-pass-banner"
        className="mt-8 flex flex-col gap-6 rounded-[1.5rem] border-2 border-dashed border-[#D97706]/45 bg-[#FFFBEB] p-6 sm:p-7 lg:flex-row lg:items-center"
      >
        <div className="shrink-0">
          <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#B45309]">
            <CalendarClock className="h-4 w-4" />
            Not ready for monthly?
          </p>
          <h3 className="mt-2 font-display text-xl font-extrabold text-[#17201A] lg:text-2xl">
            7-Day Pass
          </h3>
          <p className="mt-1">
            <span
              data-testid="weekly-pass-price"
              className="font-display text-3xl font-extrabold text-[#17201A]"
            >
              {WEEKLY_PASS.price}
            </span>
            <span className="text-sm font-semibold text-[#647067]">
              {" "}{WEEKLY_PASS.period}
            </span>
          </p>
        </div>
        <ul className="flex-1 space-y-2.5 lg:border-l lg:border-[#D97706]/25 lg:pl-7">
          {WEEKLY_PASS.features.map((f) => (
            <li
              key={f.text}
              className="flex items-center gap-2.5 text-sm font-semibold text-[#17201A]"
            >
              {f.icon === "practice" && <PenLine className="h-4 w-4 shrink-0 text-[#D97706]" />}
              {f.icon === "video" && <Play className="h-4 w-4 shrink-0 text-[#D97706]" />}
              {f.icon === "chat" && <Sparkles className="h-4 w-4 shrink-0 text-[#D97706]" />}
              {f.text}
            </li>
          ))}
          <li className="pt-1 text-xs font-bold uppercase tracking-wide text-[#B45309]">
            {WEEKLY_PASS.note}
          </li>
        </ul>
        <div className="shrink-0 lg:text-right">
          {me?.weekPassActive ? (
            <div
              data-testid="weekly-pass-active"
              className="inline-flex items-center gap-2 rounded-full bg-[#16A34A]/12 px-5 py-3.5 text-sm font-bold text-[#16A34A]"
            >
              <Check className="h-4 w-4" />
              Active · ends{" "}
              {new Date(me.weekPassExpiry).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
              })}
            </div>
          ) : (
            <button
              data-testid="weekly-pass-btn"
              data-magnetic="true"
              disabled={loadingKey !== null}
              onClick={() => handleUnlock(WEEKLY_PASS)}
              className="w-full rounded-full bg-[#D97706] px-8 py-3.5 text-sm font-bold text-white transition-[background-color,transform] duration-200 hover:bg-[#B45309] disabled:opacity-60 lg:w-auto"
            >
              {loadingKey === "Weekly:weekly" ? "Opening checkout..." : WEEKLY_PASS.cta}
            </button>
          )}
        </div>
      </div>

      <div
        data-testid="plan-grid"
        className={`mt-8 grid items-stretch gap-5 md:grid-cols-2 lg:gap-6 ${
          cards.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"
        }`}
      >
        {cards.map((p) => {
          const isTop = p.emphasis === "top";
          const key = `${p.name}:${p.billing}`;
          const isCurrent =
            !p.free &&
            currentPlan === p.name &&
            (me?.billingPeriod ?? "monthly") === p.billing;
          return (
            <div
              key={key}
              data-testid={`plan-card-${p.name.toLowerCase()}`}
              className={`relative flex flex-col rounded-[1.5rem] bg-white p-6 transition-[transform,border-color] duration-200 lg:p-7 ${
                isTop
                  ? "border-2 border-[#16A34A] shadow-sm lg:scale-[1.03]"
                  : "border border-[#E3ECE6]"
              }`}
            >
              <div className="orbit-ring" aria-hidden="true" />
              <div className="flex h-6 items-center gap-2">
                {p.badge && (
                  <span
                    data-testid={`plan-badge-${p.name.toLowerCase()}`}
                    className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${
                      isTop
                        ? "bg-[#16A34A] text-white"
                        : "bg-[#FACC15]/30 text-[#17201A]"
                    }`}
                  >
                    {p.badge}
                  </span>
                )}
                {isCurrent && (
                  <span
                    data-testid={`plan-current-${p.name.toLowerCase()}`}
                    className="rounded-full bg-[#16A34A]/12 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#16A34A]"
                  >
                    Your plan
                  </span>
                )}
              </div>
              <h3 className="mt-3 font-display text-xl font-extrabold text-[#17201A] lg:text-2xl">
                {p.name}
              </h3>
              <p className="mt-1.5">
                <span
                  data-testid={`plan-price-${p.name.toLowerCase()}`}
                  className="font-display text-3xl font-extrabold text-[#17201A] lg:text-4xl"
                >
                  {p.price}
                </span>
                <span className="text-sm font-semibold text-[#647067]">
                  {p.period}
                </span>
              </p>
              <p className="mt-1 min-h-4 text-xs font-bold text-[#16A34A]">
                {p.subtext || ""}
              </p>
              <p
                data-testid={`plan-credits-${p.name.toLowerCase()}`}
                className={`mt-3 inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold ${
                  p.creditsLocked
                    ? "bg-[#94A39B]/10 text-[#94A39B]"
                    : "bg-[#16A34A]/10 text-[#16A34A]"
                }`}
              >
                {p.creditsLocked ? (
                  <Lock className="h-3.5 w-3.5" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" />
                )}
                {p.credits}
              </p>
              {p.creditsNote && (
                <p className="mt-1.5 text-[11px] font-semibold text-[#647067]">
                  {p.creditsNote}
                </p>
              )}

              <ul className="mt-5 flex-1 space-y-2.5">
                {p.features.map((b) => (
                  <li
                    key={b.text}
                    className={`flex items-start gap-2 text-sm lg:leading-relaxed ${
                      b.locked ? "text-[#94A39B]" : "text-[#647067]"
                    }`}
                  >
                    {b.locked ? (
                      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-[#94A39B]" />
                    ) : (
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#16A34A]" />
                    )}
                    {b.text}
                  </li>
                ))}
              </ul>

              {p.free ? (
                !getToken() ? (
                  <button
                    data-testid="unlock-btn-free"
                    data-magnetic="true"
                    onClick={() => navigate("/signup")}
                    className="mt-7 w-full rounded-full border border-[#16A34A] py-3.5 text-sm font-bold text-[#16A34A] transition-[background-color,color] duration-200 hover:bg-[#16A34A] hover:text-white"
                  >
                    Start Free
                  </button>
                ) : (
                  <button
                    data-testid="unlock-btn-free"
                    disabled
                    className="mt-7 w-full cursor-default rounded-full border border-[#E3ECE6] py-3.5 text-sm font-bold text-[#647067]"
                  >
                    {currentPlan === "Free" ? "Current plan" : "Free"}
                  </button>
                )
              ) : isCurrent ? (
                <button
                  data-testid={`plan-active-${p.name.toLowerCase()}`}
                  disabled
                  className="mt-7 flex w-full cursor-default items-center justify-center gap-2 rounded-full bg-[#16A34A]/12 py-3.5 text-sm font-bold text-[#16A34A]"
                >
                  <Check className="h-4 w-4" />
                  Active plan
                </button>
              ) : (
                <button
                  data-testid={`unlock-btn-${p.name.toLowerCase()}`}
                  data-magnetic="true"
                  disabled={loadingKey !== null}
                  onClick={() => handleUnlock(p)}
                  className={`mt-7 w-full rounded-full py-3.5 text-sm font-bold transition-[background-color,transform,color] duration-200 ${
                    isTop
                      ? "bg-[#16A34A] text-white hover:bg-[#15803D]"
                      : "border border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white"
                  }`}
                >
                  {loadingKey === key ? "Opening checkout..." : p.cta}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* How Prompt Learning credits work */}
      <div
        data-testid="credit-explainer"
        className="mx-auto mt-8 max-w-2xl rounded-[1.5rem] border border-[#E3ECE6] bg-white p-6 sm:p-7"
      >
        <p className="flex items-center justify-center gap-2 text-sm font-extrabold text-[#17201A]">
          <Sparkles className="h-4 w-4 text-[#16A34A]" />
          How Prompt Learning Credits Work
        </p>
        <ul className="mx-auto mt-4 max-w-md space-y-2 text-sm font-semibold leading-relaxed text-[#647067]">
          {CREDIT_POINTS.map((c) => (
            <li key={c} className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#16A34A]" />
              {c}
            </li>
          ))}
        </ul>
      </div>

      <p
        data-testid="retention-note"
        className="mx-auto mt-5 max-w-xl text-center text-xs font-semibold leading-relaxed text-[#94A39B]"
      >
        Chat history is stored according to your plan's retention limits.
        Paid-plan conversations are retained for up to 30 days and are not
        stored permanently.
      </p>
      </>
      )}
    </div>
  );
};

const Paywall = ({ open, onClose }) => {
  // Reset nothing — PricingPlans mounts fresh each time the modal opens
  if (!open) return null;

  return (
    <div
      data-testid="paywall-modal"
      className="fixed inset-0 z-50 overflow-y-auto bg-[#17201A]/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="flex min-h-full items-center justify-center px-4 py-10">
        <div
          className="animate-fade-up relative w-full max-w-5xl overflow-hidden rounded-[2rem] border border-[#E3ECE6] bg-[#F8FAF7] p-6 shadow-sm sm:p-10 lg:max-w-7xl 2xl:max-w-[1440px]"
          onClick={(e) => e.stopPropagation()}
        >
          <PageBlobs />
          <div className="relative">
          <GlowSpecks />
          <button
            data-testid="paywall-close-btn"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-5 top-5 rounded-full border border-[#E3ECE6] bg-white p-2 text-[#647067] transition-colors duration-200 hover:text-[#17201A]"
          >
            <X className="h-4 w-4" />
          </button>

          <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-[#B45309]">
            Go unlimited
          </p>
          <h2 className="mt-2 text-center font-display text-2xl font-extrabold tracking-[-0.02em] text-[#17201A] sm:text-3xl">
            Skip the tuition fees.{" "}
            <span className="italic text-[#16A34A]">Not the syllabus.</span>
          </h2>
          <p className="mx-auto mt-2 max-w-md text-center text-sm text-[#647067]">
            One membership, unlimited practice, and real teachers on video —
            for less than a month of tuition.
          </p>

          <PricingPlans onActivated={onClose} />

          <div className="mt-8 text-center">
            <button
              data-testid="maybe-later-btn"
              onClick={onClose}
              className="text-sm font-bold text-[#647067] transition-colors duration-200 hover:text-[#16A34A]"
            >
              Maybe later
            </button>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Paywall;
